import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/ui/PageHeader'
import { Upload, CheckCircle, AlertCircle, Loader2, X, ArrowRight, AlertTriangle } from 'lucide-react'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'

interface Worker { id: string; name: string; is_active: boolean }
interface Beneficiary { id: string; name: string | null; prenom: string | null; nom: string | null }
interface Profile { id: string; user_id: string | null; email: string; workerName: string | null }

interface ParsedComment {
    rawWorkerName: string | null
    date: string | null
    contentHtml: string
    chillId: string | null   // e.g. "comment-2509"
}

interface FileEntry {
    file: File
    htmlContent: string
    comments: ParsedComment[]
    detectedWorkerNames: string[]
    mostRecentDate: string | null
    selectedBeneficiaryId: string
    autoDetectedBeneficiaryId: string
    autoDetectedCount: number
    status: 'idle' | 'importing' | 'done' | 'error'
    errorMsg?: string
    importedCount?: number
    skippedCount?: number
}

// step 1 = upload + beneficiary selection
// step 2 = review worker mapping
// step 3 = importing
type Step = 1 | 2 | 3

const FRENCH_MONTHS: Record<string, number> = {
    janvier: 1, février: 2, mars: 3, avril: 4, mai: 5, juin: 6,
    juillet: 7, août: 8, septembre: 9, octobre: 10, novembre: 11, décembre: 12,
}

const parseFrenchDate = (s: string): string | null => {
    const m = s.match(/(\d{1,2})\s+(\S+)\s+(\d{4})/)
    if (!m) return null
    const month = FRENCH_MONTHS[m[2].toLowerCase()]
    if (!month) return null
    return `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}`
}

const norm = (s: string) =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim()

const extractWorkerName = (t: string) =>
    t.replace(/^Housing\s+First\s*-\s*/i, '').replace(/\(.*?\)/g, '').trim()

const bestMatch = (rawName: string, workers: Worker[]): { worker: Worker | null; confidence: 'high' | 'low' | 'none' } => {
    if (!rawName) return { worker: null, confidence: 'none' }
    const nr = norm(rawName)
    const parts = nr.split(' ').filter(p => p.length > 2)
    let best: Worker | null = null
    let bestScore = 0

    for (const w of workers) {
        const nw = norm(w.name)
        const wParts = nw.split(' ').filter(p => p.length > 2)
        let score = 0
        if (nw === nr) score = 100
        else if (nw.includes(nr) || nr.includes(nw)) score = 80
        else {
            const common = parts.filter(p => wParts.includes(p)).length
            score = common * 30
        }
        if (score > bestScore) { bestScore = score; best = w }
    }

    if (bestScore >= 80) return { worker: best, confidence: 'high' }
    if (bestScore >= 30) return { worker: best, confidence: 'low' }
    return { worker: null, confidence: 'none' }
}

const parseHtml = (html: string): ParsedComment[] => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return Array.from(doc.querySelectorAll('.item-bloc')).flatMap(bloc => {
        const entityEl = bloc.querySelector('.chill-entity.entity-user')
        let rawWorkerName: string | null = null
        if (entityEl) {
            const txt = Array.from(entityEl.childNodes)
                .filter(n => n.nodeType === Node.TEXT_NODE)
                .map(n => n.textContent?.trim()).join(' ').trim()
            rawWorkerName = extractWorkerName(txt || entityEl.textContent || '') || null
        }
        const metaTxt = bloc.querySelector('.item-row:not(.separator) div')?.textContent || ''
        const dm = metaTxt.match(/le\s+(\d{1,2}\s+\S+\s+\d{4})/)
        const date = dm ? parseFrenchDate(dm[1]) : null
        const contentHtml = bloc.querySelector('blockquote.chill-user-quote')?.innerHTML?.trim() || ''
        // Extract Chill comment ID from the anchor: <a id="comment-2509" ...>
        const anchor = bloc.querySelector('a[id^="comment-"]')
        const chillId = anchor?.id || null
        return contentHtml ? [{ rawWorkerName, date, contentHtml, chillId }] : []
    })
}

const unique = <T,>(arr: T[]): T[] => [...new Set(arr)]

const normalizeStr = (s: string) =>
    s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

const detectBeneficiary = (
    htmlText: string,
    bens: Beneficiary[],
): { beneficiary: Beneficiary | null; count: number } => {
    const text = normalizeStr(htmlText)
    let best: Beneficiary | null = null
    let bestCount = 0

    for (const b of bens) {
        const firstName = (b.prenom || b.name || '').split(' ')[0]
        if (!firstName || firstName.length < 2) continue
        const key = normalizeStr(firstName)
        const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const matches = text.match(new RegExp(`(?<![a-z])${escaped}(?![a-z])`, 'g'))
        const count = matches?.length ?? 0
        if (count > bestCount) { bestCount = count; best = b }
    }

    return { beneficiary: bestCount > 0 ? best : null, count: bestCount }
}

// ── component ─────────────────────────────────────────────────────────────────

export const LegacyNoteImporter = ({ onNavigateHome }: { onNavigateHome: () => void }) => {
    const { user } = useAuth()
    const [workers, setWorkers] = useState<Worker[]>([])
    const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([])
    const [profiles, setProfiles] = useState<Profile[]>([])
    const [selectedCreatedBy, setSelectedCreatedBy] = useState<string>('')
    const [files, setFiles] = useState<FileEntry[]>([])
    // global worker mapping: rawName → workerId
    const [workerMapping, setWorkerMapping] = useState<Record<string, string>>({})
    const [step, setStep] = useState<Step>(1)
    const [importing, setImporting] = useState(false)

    useEffect(() => {
        supabase.from('workers').select('id,name,is_active').order('name')
            .then(({ data }) => setWorkers(data || []))
        supabase.from('beneficiaries').select('id,prenom,nom,name')
            .order('nom')
            .then(({ data }) => setBeneficiaries((data as Beneficiary[]) || []))
        supabase.from('profiles').select('id,user_id,email,worker_id,workers(name)').is('deleted_dt', null)
            .then(({ data }) => {
                const list: Profile[] = (data || []).map((p: any) => ({
                    id: p.id,
                    user_id: p.user_id ?? null,
                    email: p.email,
                    workerName: p.workers?.name || null,
                }))
                setProfiles(list)
                // Default to current user — use user_id (auth UUID) for created_by
                const current = list.find(p => p.user_id === user?.id)
                if (current) setSelectedCreatedBy(current.user_id!)
                else if (list.length > 0) setSelectedCreatedBy(list[0].user_id || list[0].id)
            })
    }, [])

    // All unique raw worker names across all files
    const allWorkerNames = unique(files.flatMap(f => f.detectedWorkerNames))

    // When workers load or files change, rebuild mapping (keep manual overrides)
    useEffect(() => {
        if (!workers.length) return
        setWorkerMapping(prev => {
            const next = { ...prev }
            for (const name of allWorkerNames) {
                if (next[name] !== undefined) continue   // keep override
                const { worker } = bestMatch(name, workers)
                next[name] = worker?.id || ''
            }
            return next
        })
    }, [workers, files])

    const handleFiles = async (fileList: FileList) => {
        const entries: FileEntry[] = []
        for (const file of Array.from(fileList)) {
            if (!file.name.match(/\.html?$/i)) continue
            const html = await file.text()
            const comments = parseHtml(html)
            const detectedWorkerNames = unique(comments.map(c => c.rawWorkerName).filter(Boolean) as string[])
            const parsedDates = comments.map(c => c.date).filter((d): d is string => Boolean(d))
            const mostRecentDate = parsedDates.length
                ? parsedDates.reduce((a, b) => (a > b ? a : b))
                : null
            const { beneficiary: detected, count } = detectBeneficiary(html, beneficiaries)
            entries.push({
                file, htmlContent: html, comments, detectedWorkerNames,
                mostRecentDate,
                selectedBeneficiaryId: detected?.id || '',
                autoDetectedBeneficiaryId: detected?.id || '',
                autoDetectedCount: count,
                status: 'idle',
            })
        }
        setFiles(prev => [...prev, ...entries])
    }

    const updateFile = (idx: number, patch: Partial<FileEntry>) =>
        setFiles(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f))

    const canProceedToStep2 =
        files.length > 0 && files.every(f => f.selectedBeneficiaryId)

    const selectedIds = files.map(f => f.selectedBeneficiaryId).filter(Boolean)
    const duplicateIds = new Set(selectedIds.filter((id, i) => selectedIds.indexOf(id) !== i))

    const unresolvedNames = allWorkerNames.filter(n => !workerMapping[n])

    const handleImport = async () => {
        setStep(3)
        setImporting(true)

        for (let i = 0; i < files.length; i++) {
            const f = files[i]
            updateFile(i, { status: 'importing' })
            let imported = 0
            let skipped = 0
            let lastErr: string | null = null

            // Fetch existing note contents for this beneficiary (for deduplication)
            const { data: existingNotes } = await supabase
                .from('notes')
                .select('note')
                .eq('beneficiary_id', f.selectedBeneficiaryId)
            const existingContents = new Set((existingNotes || []).map(n => n.note?.trim()))

            for (const c of f.comments) {
                const workerId = c.rawWorkerName ? (workerMapping[c.rawWorkerName] || null) : null

                // Deduplicate: skip if exact content already exists
                if (existingContents.has(c.contentHtml)) { skipped++; continue }

                const noteDate = c.date || f.mostRecentDate || new Date().toISOString().split('T')[0]
                const { error } = await supabase.from('notes').insert({
                    beneficiary_id: f.selectedBeneficiaryId,
                    worker_id: workerId,
                    created_by: selectedCreatedBy || user?.id || null,
                    note_date: noteDate,
                    note: c.contentHtml,
                    comment: !workerId && c.rawWorkerName ? c.rawWorkerName : null,
                    submit_ts: new Date().toISOString(),
                    colleague_ids: null,
                })
                if (error) {
                    lastErr = error.message
                } else {
                    imported++
                    existingContents.add(c.contentHtml)  // prevent double-insert within same run
                }
            }

            updateFile(i, {
                status: imported > 0 || skipped > 0 ? 'done' : 'error',
                importedCount: imported,
                skippedCount: skipped,
                errorMsg: lastErr || undefined,
            })
        }
        setImporting(false)
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Import Legacy Notes" icon={<Upload size={20} />} onBack={onNavigateHome} />

            <main className="max-w-3xl mx-auto py-8 px-4 space-y-6">

                {/* ── Created by selector (always visible) ── */}
                {step !== 3 && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Créé par <span className="text-gray-400 font-normal">(user ID utilisé pour created_by)</span>
                        </label>
                        <select
                            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            value={selectedCreatedBy}
                            onChange={e => setSelectedCreatedBy(e.target.value)}
                        >
                            {profiles.map(p => (
                                <option key={p.id} value={p.user_id || p.id}>
                                    {p.workerName ? `${p.workerName} — ${p.email}` : p.email}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* ── STEP 1 : upload + beneficiary ── */}
                {step === 1 && (
                    <>
                        <label
                            className="block border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files) }}
                        >
                            <Upload className="mx-auto mb-3 text-gray-400" size={32} />
                            <p className="text-gray-600 font-medium">Déposer les fichiers HTML ici</p>
                            <p className="text-sm text-gray-400 mt-1">Une page HTML Chill par bénéficiaire</p>
                            <input type="file" accept=".html,.htm" multiple className="hidden"
                                onChange={e => e.target.files && handleFiles(e.target.files)} />
                        </label>

                        {files.map((f, fi) => {
                            const isDuplicate = !!f.selectedBeneficiaryId && duplicateIds.has(f.selectedBeneficiaryId)
                            const notDetected = !f.autoDetectedBeneficiaryId
                            const detectedBen = f.autoDetectedBeneficiaryId
                                ? beneficiaries.find(b => b.id === f.autoDetectedBeneficiaryId)
                                : null
                            return (
                                <div key={fi} className={`bg-white rounded-lg shadow border p-4 ${isDuplicate ? 'border-orange-300' : 'border-gray-100'}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="font-medium text-sm text-gray-800 truncate">{f.file.name}</span>
                                        <div className="flex items-center gap-3 shrink-0 ml-2">
                                            <span className="text-xs text-gray-400">{f.comments.length} commentaire{f.comments.length > 1 ? 's' : ''}</span>
                                            <button onClick={() => setFiles(p => p.filter((_, i) => i !== fi))}
                                                className="text-gray-300 hover:text-gray-500"><X size={14} /></button>
                                        </div>
                                    </div>

                                    {/* Auto-detection feedback */}
                                    {detectedBen && !notDetected && (
                                        <p className="text-xs text-green-600 mb-2 flex items-center gap-1">
                                            <CheckCircle size={11} />
                                            Détecté automatiquement : <strong>{displayBeneficiaryName(detectedBen)}</strong>
                                            <span className="text-gray-400">({f.autoDetectedCount} occurrence{f.autoDetectedCount > 1 ? 's' : ''})</span>
                                        </p>
                                    )}
                                    {notDetected && (
                                        <p className="text-xs text-orange-500 mb-2 flex items-center gap-1">
                                            <AlertTriangle size={11} />
                                            Aucun bénéficiaire détecté — sélectionner manuellement
                                        </p>
                                    )}
                                    {isDuplicate && (
                                        <p className="text-xs text-orange-500 mb-2 flex items-center gap-1">
                                            <AlertTriangle size={11} />
                                            Même bénéficiaire sélectionné dans un autre fichier
                                        </p>
                                    )}

                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Bénéficiaire *</label>
                                        <select
                                            className={`w-full text-sm border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 ${notDetected ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}
                                            value={f.selectedBeneficiaryId}
                                            onChange={e => updateFile(fi, { selectedBeneficiaryId: e.target.value })}
                                        >
                                            <option value="">— Sélectionner —</option>
                                            {beneficiaries.map(b => (
                                                <option key={b.id} value={b.id}>{displayBeneficiaryName(b)}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )
                        })}

                        {files.length > 0 && (
                            <div className="flex justify-end">
                                <Button onClick={() => setStep(2)} disabled={!canProceedToStep2}
                                    className="flex items-center gap-2">
                                    Confirmer le mapping des travailleurs
                                    <ArrowRight size={14} />
                                </Button>
                            </div>
                        )}
                    </>
                )}

                {/* ── STEP 2 : review worker mapping ── */}
                {step === 2 && (
                    <>
                        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                                <h2 className="font-semibold text-sm text-gray-800">
                                    Mapping des travailleurs détectés
                                </h2>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Vérifie et corrige les associations avant l'import.
                                    Les noms non mappés seront importés avec <code>worker_id = null</code> et le nom brut en commentaire.
                                </p>
                            </div>

                            <div className="divide-y divide-gray-50">
                                {allWorkerNames.map(name => {
                                    const { worker: autoWorker, confidence } = bestMatch(name, workers)
                                    const currentId = workerMapping[name] || ''
                                    const currentWorker = workers.find(w => w.id === currentId)
                                    const count = files.flatMap(f => f.comments)
                                        .filter(c => c.rawWorkerName === name).length

                                    return (
                                        <div key={name} className="flex items-center gap-4 px-4 py-3">
                                            {/* Status dot */}
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                                                currentId ? 'bg-green-400' : 'bg-amber-400'
                                            }`} />

                                            {/* Detected name */}
                                            <div className="w-52 shrink-0">
                                                <p className="text-sm font-medium text-gray-800">{name}</p>
                                                <p className="text-xs text-gray-400">{count} note{count > 1 ? 's' : ''}</p>
                                            </div>

                                            {/* Arrow */}
                                            <ArrowRight size={14} className="text-gray-300 shrink-0" />

                                            {/* Dropdown */}
                                            <div className="flex-1">
                                                <select
                                                    className={`w-full text-sm border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                                                        currentId ? 'border-gray-200' : 'border-amber-300 bg-amber-50'
                                                    }`}
                                                    value={currentId}
                                                    onChange={e => setWorkerMapping(prev => ({ ...prev, [name]: e.target.value }))}
                                                >
                                                    <option value="">— Importer sans travailleur —</option>
                                                    {workers.map(w => <option key={w.id} value={w.id}>{w.name}{!w.is_active ? ' (inactif)' : ''}</option>)}
                                                </select>
                                                {confidence === 'low' && currentWorker && autoWorker?.id === currentId && (
                                                    <p className="text-xs text-amber-600 mt-0.5">Match incertain — vérifie</p>
                                                )}
                                                {confidence === 'high' && autoWorker?.id === currentId && (
                                                    <p className="text-xs text-green-600 mt-0.5">Match automatique</p>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Summary per file */}
                        <div className="space-y-2">
                            {files.map((f, fi) => {
                                const total = f.comments.length
                                const mapped = f.comments.filter(c => c.rawWorkerName && workerMapping[c.rawWorkerName]).length
                                const unmapped = f.comments.filter(c => c.rawWorkerName && !workerMapping[c.rawWorkerName]).length
                                return (
                                    <div key={fi} className="flex items-center justify-between bg-white rounded border border-gray-100 px-4 py-2 text-sm">
                                        <span className="text-gray-700 truncate">{f.file.name}</span>
                                        <span className="ml-4 shrink-0 text-gray-600">
                                            {total} notes
                                            {unmapped > 0 && <span className="text-amber-600 ml-1">· {unmapped} sans travailleur</span>}
                                            {mapped === total && <span className="text-green-600 ml-1">· tous mappés</span>}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>

                        <div className="flex items-center justify-between">
                            <Button variant="outline" onClick={() => setStep(1)}>Retour</Button>
                            <Button onClick={handleImport} className="flex items-center gap-2">
                                Importer
                                <ArrowRight size={14} />
                            </Button>
                        </div>
                    </>
                )}

                {/* ── STEP 3 : results ── */}
                {step === 3 && (
                    <div className="space-y-3">
                        <h2 className="font-semibold text-gray-800">Résultats de l'import</h2>
                        {files.map((f, fi) => (
                            <div key={fi} className="bg-white rounded-lg border border-gray-100 shadow px-4 py-3 flex items-center gap-3">
                                {f.status === 'done' && <CheckCircle size={16} className="text-green-500 shrink-0" />}
                                {f.status === 'error' && <AlertCircle size={16} className="text-red-500 shrink-0" />}
                                {f.status === 'importing' && <Loader2 size={16} className="text-blue-400 animate-spin shrink-0" />}
                                {f.status === 'idle' && <div className="w-4 h-4 shrink-0" />}
                                <span className="text-sm text-gray-700 truncate flex-1">{f.file.name}</span>
                                <span className="text-xs shrink-0">
                                    {f.status === 'done' && (
                                        <span className="text-green-600">
                                            {f.importedCount} importée{(f.importedCount || 0) > 1 ? 's' : ''}
                                            {(f.skippedCount || 0) > 0 && `, ${f.skippedCount} déjà présente${(f.skippedCount || 0) > 1 ? 's' : ''}`}
                                            {f.errorMsg ? ` · ${f.errorMsg}` : ''}
                                        </span>
                                    )}
                                    {f.status === 'error' && <span className="text-red-600">{f.errorMsg}</span>}
                                    {f.status === 'importing' && <span className="text-gray-400">En cours...</span>}
                                </span>
                            </div>
                        ))}
                        {!importing && (
                            <div className="flex justify-end pt-2">
                                <Button onClick={onNavigateHome}>Terminer</Button>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    )
}
